import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, Save, User, Stethoscope, MapPin, Phone } from "lucide-react";
import { LivingSituationSelect } from "@/components/LivingSituationSelect";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export default function NewClientPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Info
    first_name: "",
    last_name: "",
    dob: "",
    ssn: "",
    gender: "",
    living_situation: "",
    phone: "",
    email: "",
    // Address
    address: "",
    city: "",
    state: "",
    zip_code: "",
    // Emergency Contact
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relation: "",
    // Insurance
    insurance_info: "",
    insurance_company: "",
    insurance_policy_number: "",
    medicaid_id: "",
    medicare_id: "",
    insurance_accepted: false,
    // Medical
    primary_diagnosis: "",
    secondary_diagnoses: "",
    care_reasons: "",
    is_diabetic: false,
    has_seizure_history: false,
    allergies: "",
    height: "",
    weight: "",
    dme_current: "",
    dme_needed: "",
    // Physician
    physician_name: "",
    physician_clinic: "",
    physician_phone: "",
    physician_fax: "",
    physician_address: "",
    last_md_visit: "",
    // Specialist
    has_specialist: false,
    specialist_name: "",
    // Previous Agency
    has_previous_agency: false,
    previous_agency: "",
    // Preferred PCW
    preferred_pcw_name: "",
    preferred_pcw_phone: "",
    // Notes
    notes: "",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name) {
      toast.error("Please provide first and last name");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        dob: formData.dob || null,
        ssn: formData.ssn.replace(/\D/g, "") || null,
        gender: formData.gender || null,
        living_situation: formData.living_situation || null,
        phone: formData.phone || null,
        email: formData.email || null,
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        zip_code: formData.zip_code || null,
        emergency_contact_name: formData.emergency_contact_name || null,
        emergency_contact_phone: formData.emergency_contact_phone || null,
        emergency_contact_relation: formData.emergency_contact_relation || null,
        insurance_info: formData.insurance_info || null,
        insurance_company: formData.insurance_company || null,
        insurance_policy_number: formData.insurance_policy_number || null,
        medicaid_id: formData.medicaid_id || null,
        medicare_id: formData.medicare_id || null,
        insurance_accepted: formData.insurance_accepted,
        primary_diagnosis: formData.primary_diagnosis || null,
        secondary_diagnoses: formData.secondary_diagnoses
          ? formData.secondary_diagnoses.split(",").map((d) => d.trim())
          : [],
        care_reasons: formData.care_reasons || null,
        is_diabetic: formData.is_diabetic,
        has_seizure_history: formData.has_seizure_history,
        allergies: formData.allergies || null,
        height: formData.height || null,
        weight: formData.weight || null,
        dme_current: formData.dme_current || null,
        dme_needed: formData.dme_needed || null,
        physician: {
          name: formData.physician_name || null,
          clinic_name: formData.physician_clinic || null,
          phone: formData.physician_phone || null,
          fax: formData.physician_fax || null,
          address: formData.physician_address || null,
        },
        last_md_visit: formData.last_md_visit || null,
        has_specialist: formData.has_specialist,
        specialist_name: formData.specialist_name || null,
        has_previous_agency: formData.has_previous_agency,
        previous_agency: formData.previous_agency || null,
        preferred_pcw_name: formData.preferred_pcw_name || null,
        preferred_pcw_phone: formData.preferred_pcw_phone || null,
        notes: formData.notes || null,
      };

      const response = await axios.post(`${API}/clients`, payload);
      toast.success("Client created successfully!");
      navigate(`/clients/${response.data.id}`);
    } catch (error) {
      console.error("Failed to create client:", error);
      toast.error(error.response?.data?.detail || "Failed to create client");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { id: 1, title: "Personal Info", icon: User },
    { id: 2, title: "Medical", icon: Stethoscope },
    { id: 3, title: "Care Details", icon: Phone },
  ];

  return (
    <Layout>
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/clients" className="hover:text-slate-700">
          Clients
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">New Client</span>
      </div>

      {/* Back Button */}
      <Link
        to="/clients"
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-6"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Clients
      </Link>

      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">Add New Client</h1>
        <p className="page-subtitle">Enter client information to create a new record.</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4 mb-8">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isCompleted = currentStep > step.id;
          
          return (
            <div key={step.id} className="flex items-center">
              <button
                onClick={() => setCurrentStep(step.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive
                    ? "bg-primary text-white"
                    : isCompleted
                    ? "bg-primary/10 text-primary"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline text-sm font-medium">{step.title}</span>
              </button>
              {index < steps.length - 1 && (
                <div className="w-8 h-px bg-slate-200 mx-2"></div>
              )}
            </div>
          );
        })}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Step 1: Personal Information */}
        {currentStep === 1 && (
          <div className="space-y-6 animate-fade-in">
            {/* Basic Info */}
            <div className="form-section">
              <h3 className="form-section-title">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    data-testid="client-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    data-testid="client-last-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dob">Date of Birth</Label>
                  <Input
                    id="dob"
                    name="dob"
                    type="date"
                    value={formData.dob}
                    onChange={handleChange}
                    data-testid="client-dob"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <Select value={formData.gender || ""} onValueChange={(v) => handleSelectChange("gender", v)}>
                    <SelectTrigger data-testid="client-gender">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="unknown">Unknown / not stated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ssn">Social Security Number</Label>
                  <Input
                    id="ssn"
                    name="ssn"
                    inputMode="numeric"
                    maxLength={11}
                    placeholder="123-45-6789"
                    value={formData.ssn}
                    onChange={(e) => {
                      const d = e.target.value.replace(/\D/g, "").slice(0, 9);
                      const v = d.length > 5 ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}` : d.length > 3 ? `${d.slice(0, 3)}-${d.slice(3)}` : d;
                      setFormData((prev) => ({ ...prev, ssn: v }));
                    }}
                    data-testid="client-ssn"
                  />
                  <p className="text-xs text-slate-500">Stored encrypted; only the last 4 digits are shown afterwards. Used to auto-fill state forms (e.g. PCST).</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={handleChange}
                    data-testid="client-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="patient@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="client-email"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="form-section">
              <h3 className="form-section-title">Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    data-testid="client-address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    data-testid="client-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Select
                    value={formData.state}
                    onValueChange={(value) => handleSelectChange("state", value)}
                  >
                    <SelectTrigger data-testid="client-state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip_code">ZIP Code</Label>
                  <Input
                    id="zip_code"
                    name="zip_code"
                    maxLength={10}
                    value={formData.zip_code}
                    onChange={handleChange}
                    data-testid="client-zip"
                  />
                </div>
                <LivingSituationSelect value={formData.living_situation} onChange={(v) => handleSelectChange("living_situation", v)} testId="client-living-situation" />
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="form-section">
              <h3 className="form-section-title">Emergency Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_name">Name</Label>
                  <Input
                    id="emergency_contact_name"
                    name="emergency_contact_name"
                    value={formData.emergency_contact_name}
                    onChange={handleChange}
                    data-testid="emergency-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_phone">Phone</Label>
                  <Input
                    id="emergency_contact_phone"
                    name="emergency_contact_phone"
                    type="tel"
                    value={formData.emergency_contact_phone}
                    onChange={handleChange}
                    data-testid="emergency-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_relation">Relationship</Label>
                  <Input
                    id="emergency_contact_relation"
                    name="emergency_contact_relation"
                    placeholder="e.g., Spouse, Child"
                    value={formData.emergency_contact_relation}
                    onChange={handleChange}
                    data-testid="emergency-relation"
                  />
                </div>
              </div>
            </div>

            {/* Insurance */}
            <div className="form-section">
              <h3 className="form-section-title">Insurance Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="insurance_company">Insurance Company</Label>
                  <Input
                    id="insurance_company"
                    name="insurance_company"
                    placeholder="e.g., UnitedHealthcare, Aetna, Anthem BCBS"
                    value={formData.insurance_company}
                    onChange={handleChange}
                    data-testid="insurance-company"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="insurance_policy_number">Policy / Member Number</Label>
                  <Input
                    id="insurance_policy_number"
                    name="insurance_policy_number"
                    placeholder="Policy or member ID"
                    value={formData.insurance_policy_number}
                    onChange={handleChange}
                    data-testid="insurance-policy-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="medicaid_id">Medicaid ID Number</Label>
                  <Input
                    id="medicaid_id"
                    name="medicaid_id"
                    placeholder="Medicaid Recipient ID"
                    value={formData.medicaid_id}
                    onChange={handleChange}
                    data-testid="medicaid-id"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="medicare_id">Medicare ID (MBI)</Label>
                  <Input
                    id="medicare_id"
                    name="medicare_id"
                    placeholder="Medicare Beneficiary ID"
                    value={formData.medicare_id}
                    onChange={handleChange}
                    data-testid="medicare-id"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="insurance_info">Additional Insurance Notes</Label>
                  <Input
                    id="insurance_info"
                    name="insurance_info"
                    placeholder="Group #, secondary insurance, authorization notes, etc."
                    value={formData.insurance_info}
                    onChange={handleChange}
                    data-testid="insurance-info"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="insurance_accepted"
                    checked={formData.insurance_accepted}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange("insurance_accepted", checked)
                    }
                    data-testid="insurance-accepted"
                  />
                  <Label htmlFor="insurance_accepted">Insurance Accepted</Label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Medical Information */}
        {currentStep === 2 && (
          <div className="space-y-6 animate-fade-in">
            {/* Diagnoses */}
            <div className="form-section">
              <h3 className="form-section-title">Diagnoses</h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primary_diagnosis">Primary Diagnosis</Label>
                  <Input
                    id="primary_diagnosis"
                    name="primary_diagnosis"
                    value={formData.primary_diagnosis}
                    onChange={handleChange}
                    data-testid="primary-diagnosis"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondary_diagnoses">
                    Secondary Diagnoses (comma-separated)
                  </Label>
                  <Input
                    id="secondary_diagnoses"
                    name="secondary_diagnoses"
                    placeholder="Diabetes, Hypertension, etc."
                    value={formData.secondary_diagnoses}
                    onChange={handleChange}
                    data-testid="secondary-diagnoses"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="care_reasons">Reason for Home Care</Label>
                  <Textarea
                    id="care_reasons"
                    name="care_reasons"
                    rows={3}
                    value={formData.care_reasons}
                    onChange={handleChange}
                    data-testid="care-reasons"
                  />
                </div>
              </div>
            </div>

            {/* Medical Conditions */}
            <div className="form-section">
              <h3 className="form-section-title">Medical Conditions (Alerts)</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_diabetic"
                    checked={formData.is_diabetic}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange("is_diabetic", checked)
                    }
                    data-testid="is-diabetic"
                  />
                  <Label htmlFor="is_diabetic">
                    Client is Diabetic (triggers blood sugar monitoring alerts)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="has_seizure_history"
                    checked={formData.has_seizure_history}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange("has_seizure_history", checked)
                    }
                    data-testid="has-seizure"
                  />
                  <Label htmlFor="has_seizure_history">
                    Client has Seizure History (triggers seizure protocol alerts)
                  </Label>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="allergies">Allergies</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setFormData((prev) => ({ ...prev, allergies: "NKDA (No Known Drug Allergies)" }))}
                      data-testid="client-nkda-preset-btn"
                    >
                      NKDA
                    </Button>
                  </div>
                  <Textarea
                    id="allergies"
                    name="allergies"
                    value={formData.allergies}
                    onChange={handleChange}
                    placeholder="e.g., Penicillin, latex, peanuts (or NKDA if none)"
                    rows={2}
                    data-testid="client-allergies-input"
                  />
                  <p className="text-xs text-slate-500">
                    Auto-fills into the CMS-485 and other clinical forms.
                  </p>
                </div>
              </div>
            </div>

            {/* DME */}
            <div className="form-section">
              <h3 className="form-section-title">Physical Information &amp; DME Supplies</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="height">Height</Label>
                  <Input
                    id="height"
                    name="height"
                    placeholder={'e.g., 5\'6"'}
                    value={formData.height}
                    onChange={handleChange}
                    data-testid="client-height"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weight">Weight</Label>
                  <Input
                    id="weight"
                    name="weight"
                    placeholder="e.g., 165 lbs"
                    value={formData.weight}
                    onChange={handleChange}
                    data-testid="client-weight"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dme_current">Current DME (in use)</Label>
                  <Textarea
                    id="dme_current"
                    name="dme_current"
                    rows={2}
                    placeholder="List current equipment"
                    value={formData.dme_current}
                    onChange={handleChange}
                    data-testid="dme-current"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dme_needed">DME Needed</Label>
                  <Textarea
                    id="dme_needed"
                    name="dme_needed"
                    rows={2}
                    placeholder="List needed equipment"
                    value={formData.dme_needed}
                    onChange={handleChange}
                    data-testid="dme-needed"
                  />
                </div>
              </div>
            </div>

            {/* Physician */}
            <div className="form-section">
              <h3 className="form-section-title">Physician Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="physician_name">Physician Name</Label>
                  <Input
                    id="physician_name"
                    name="physician_name"
                    value={formData.physician_name}
                    onChange={handleChange}
                    data-testid="physician-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="physician_clinic">Clinic Name</Label>
                  <Input
                    id="physician_clinic"
                    name="physician_clinic"
                    value={formData.physician_clinic}
                    onChange={handleChange}
                    data-testid="physician-clinic"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="physician_phone">Phone</Label>
                  <Input
                    id="physician_phone"
                    name="physician_phone"
                    type="tel"
                    value={formData.physician_phone}
                    onChange={handleChange}
                    data-testid="physician-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="physician_fax">Fax</Label>
                  <Input
                    id="physician_fax"
                    name="physician_fax"
                    type="tel"
                    value={formData.physician_fax}
                    onChange={handleChange}
                    data-testid="physician-fax"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_md_visit">Last MD Visit</Label>
                  <Input
                    id="last_md_visit"
                    name="last_md_visit"
                    type="date"
                    value={formData.last_md_visit}
                    onChange={handleChange}
                    data-testid="last-md-visit"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Care Details */}
        {currentStep === 3 && (
          <div className="space-y-6 animate-fade-in">
            {/* Specialist */}
            <div className="form-section">
              <h3 className="form-section-title">Specialist Information</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="has_specialist"
                    checked={formData.has_specialist}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange("has_specialist", checked)
                    }
                    data-testid="has-specialist"
                  />
                  <Label htmlFor="has_specialist">Client has a Specialist</Label>
                </div>
                {formData.has_specialist && (
                  <div className="space-y-2">
                    <Label htmlFor="specialist_name">Specialist Name</Label>
                    <Input
                      id="specialist_name"
                      name="specialist_name"
                      value={formData.specialist_name}
                      onChange={handleChange}
                      data-testid="specialist-name"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Previous Agency */}
            <div className="form-section">
              <h3 className="form-section-title">Previous Agency</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="has_previous_agency"
                    checked={formData.has_previous_agency}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange("has_previous_agency", checked)
                    }
                    data-testid="has-previous-agency"
                  />
                  <Label htmlFor="has_previous_agency">
                    Client was with a previous agency
                  </Label>
                </div>
                {formData.has_previous_agency && (
                  <div className="space-y-2">
                    <Label htmlFor="previous_agency">Previous Agency Name</Label>
                    <Input
                      id="previous_agency"
                      name="previous_agency"
                      value={formData.previous_agency}
                      onChange={handleChange}
                      data-testid="previous-agency"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Preferred PCW */}
            <div className="form-section">
              <h3 className="form-section-title">Preferred Personal Care Worker</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="preferred_pcw_name">PCW Name</Label>
                  <Input
                    id="preferred_pcw_name"
                    name="preferred_pcw_name"
                    value={formData.preferred_pcw_name}
                    onChange={handleChange}
                    data-testid="preferred-pcw-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preferred_pcw_phone">PCW Phone</Label>
                  <Input
                    id="preferred_pcw_phone"
                    name="preferred_pcw_phone"
                    type="tel"
                    value={formData.preferred_pcw_phone}
                    onChange={handleChange}
                    data-testid="preferred-pcw-phone"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="form-section">
              <h3 className="form-section-title">Additional Notes</h3>
              <div className="space-y-2">
                <Textarea
                  id="notes"
                  name="notes"
                  rows={4}
                  placeholder="Any additional notes about the client..."
                  value={formData.notes}
                  onChange={handleChange}
                  data-testid="client-notes"
                />
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
          <div>
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setCurrentStep(currentStep - 1)}
              >
                Previous
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            {currentStep < 3 ? (
              <Button type="button" onClick={() => setCurrentStep(currentStep + 1)}>
                Next
              </Button>
            ) : (
              <Button type="submit" disabled={loading} data-testid="submit-client-btn">
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner"></span>
                    Creating...
                  </span>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create Client
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </form>
    </Layout>
  );
}
